// src/pages/Landing.tsx
import { FC } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  KeyIcon,
  UserCircleIcon,
  ArrowPathIcon,
  LockClosedIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

import FaceIDClip from '../assets/face_id_clip.jpg';
import firelightDetailsScreenshot from '../assets/firelight-details.png';
import earnScreenshot from '../assets/earn-cropped-screenshot.png';
import mintScreenshot from '../assets/minting_fxrp_screenshot.jpeg';
import appStoreBadge from '../assets/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg';
import Footer from '../components/footer/footer';
import Header from '../components/header/header';
import BuiltBySection from '../components/sections/built-by/built-by';
import HowItWorksSection from '../components/sections/how-it-works/how-it-works';
import SecuritySection from '../components/sections/security-section/security-section';
import BuiltOnFlareSection from '../components/sections/built-on-flare/built-on-flare';
import {
  APP_STORE_URL,
  FYEO_AUDIT_URL,
  FXRP_GUIDE_URL,
  TUTORIALS_URL
} from '../constants/links';

const AUREUS = {
  green: '#6FC2AE',
  blue: '#609EBD',
  navy: '#01263D',
  purple: '#4643D3'
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 }
  }
};

const Landing: FC = () => {
  return (
    <div
      className="min-h-screen text-white"
      style={{
        background: `linear-gradient(to bottom, ${AUREUS.navy}, #0a1a2f)`
      }}
    >
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-24 md:pt-48 md:pb-36 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-1/3 -left-20 w-96 h-96 rounded-full blur-3xl opacity-40"
            style={{ background: AUREUS.green }}
          />
          <div
            className="absolute bottom-1/4 -right-20 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-30"
            style={{ background: AUREUS.blue }}
          />
        </div>

        <div className="relative mx-auto max-w-5xl px-6 text-center">
          <motion.h1
            className="text-5xl md:text-7xl font-black tracking-tight leading-tight bg-clip-text mb-6"
            style={{
              background: `linear-gradient(90deg, ${AUREUS.green}, ${AUREUS.blue}, ${AUREUS.purple})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            Own your crypto.
            <br />
            Own your name.
            <br />
            Own your keys.
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto mb-8 leading-relaxed"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            Non-custodial Flare wallet. Passkeys, FXRP mint &amp; redeem, and
            yield — in one iOS app.
          </motion.p>

          <motion.div
            className="mb-10 opacity-90"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-slate-800/60 border border-slate-600/50 text-sm md:text-base font-medium">
              <span style={{ color: AUREUS.green }}>New:</span>
              One-Tap FXRP Mint &amp; Redeem → Bridge XRP to Flare DeFi
            </div>
          </motion.div>

          <motion.div
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex transition-transform hover:scale-[1.03]"
              aria-label="Download Oxen Flow on the App Store"
            >
              <img
                src={appStoreBadge}
                alt="Download on the App Store"
                className="h-14 w-auto"
              />
            </a>

            <a
              href={TUTORIALS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-10 py-4 rounded-2xl font-medium transition-all"
              style={{
                background: 'rgba(70, 67, 211, 0.12)',
                border: `1px solid rgba(96, 158, 189, 0.35)`,
                color: AUREUS.blue
              }}
            >
              How it works → Watch tutorials
            </a>
          </motion.div>

          <motion.p
            className="text-base md:text-lg text-slate-400 mt-5"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            Currently available on iOS
          </motion.p>

          <motion.div
            className="mt-14 flex flex-wrap justify-center gap-8 md:gap-10 text-sm"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.a
              href={FYEO_AUDIT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-100 opacity-90 transition-opacity underline-offset-4 hover:underline"
              variants={fadeInUp}
            >
              <ShieldCheckIcon
                className="h-5 w-5 shrink-0"
                style={{ color: AUREUS.green }}
              />
              <span>FYEO Audited</span>
            </motion.a>
            <motion.div
              className="flex items-center gap-2"
              variants={fadeInUp}
            >
              <LockClosedIcon
                className="h-5 w-5 shrink-0"
                style={{ color: AUREUS.blue }}
              />
              <span>Non-custodial</span>
            </motion.div>
            <motion.div
              className="flex items-center gap-2"
              variants={fadeInUp}
            >
              <KeyIcon
                className="h-5 w-5 shrink-0"
                style={{ color: AUREUS.purple }}
              />
              <span>Passkeys + Web3Auth</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <motion.div
        className="mt-4 md:mt-8 relative mx-auto max-w-[420px] md:max-w-[500px] px-6"
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.9 }}
      >
        <div
          className="absolute inset-0 rounded-3xl blur-3xl opacity-30"
          style={{
            background: `radial-gradient(circle at 30% 70%, ${AUREUS.green}40, transparent 60%)`
          }}
        />

        <div className="relative rounded-[3rem] overflow-hidden shadow-2xl shadow-black/40 border border-slate-700/30">
          <img
            src={firelightDetailsScreenshot}
            alt="Oxen Flow app showing Firelight yield details on Flare"
            className="w-full h-auto"
            width={500}
            height={1000}
            decoding="async"
          />
        </div>

        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-5 py-2 bg-slate-900/80 backdrop-blur-md rounded-full text-xs font-medium border border-slate-700 whitespace-nowrap">
          Now with Firelight Yield
        </div>
      </motion.div>

      {/* Features */}
      <section
        id="features"
        className="py-24 mt-16"
        style={{ background: 'rgba(1, 38, 61, 0.4)' }}
      >
        <div className="mx-auto max-w-6xl px-6">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-black mb-5">
              Crypto made <span style={{ color: AUREUS.green }}>simple</span>
            </h2>
            <p className="text-xl opacity-80 max-w-2xl mx-auto">
              No seed phrases. Beautiful UX. True ownership.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {(
              [
                {
                  Icon: KeyIcon,
                  title: 'Passkey login',
                  img: FaceIDClip,
                  alt: 'Oxen Flow passkey login with Face ID',
                  desc: 'Face ID, Touch ID or security key — no more seed phrases',
                  color: AUREUS.green
                },
                {
                  Icon: ArrowPathIcon,
                  title: 'One-Tap FXRP Mint & Redeem',
                  img: mintScreenshot,
                  alt: 'Oxen Flow one-tap FXRP mint screen',
                  desc: 'Deposit XRP → Mint FXRP → Earn yield via Firelight or Kinetic → Redeem back to XRP anytime. All inside the app — no external sites or connections required.',
                  color: AUREUS.green
                },
                {
                  Icon: SparklesIcon,
                  title: 'Effortless yield',
                  img: earnScreenshot,
                  alt: 'Oxen Flow earn screen with yield opportunities on Flare',
                  desc: 'Stake, delegate and earn — beautifully simple',
                  color: AUREUS.blue
                },
                {
                  Icon: UserCircleIcon,
                  title: 'Own your identity',
                  desc: 'Get & trade your personal username',
                  color: AUREUS.purple
                },
                {
                  Icon: LockClosedIcon,
                  title: 'True self-custody',
                  desc: 'You control the keys. Always.',
                  color: AUREUS.green
                },
                {
                  Icon: ArrowPathIcon,
                  title: 'Fast & cheap',
                  desc: 'Flare speed — sub-second tx, tiny fees',
                  color: AUREUS.blue
                }
              ] as const
            ).map((f, i) =>
              'img' in f && f.img ? (
                <motion.div
                  key={i}
                  className="rounded-2xl p-8 transition-all group border overflow-hidden"
                  variants={fadeInUp}
                  style={{
                    background: 'rgba(1, 38, 61, 0.45)',
                    borderColor: 'rgba(96, 158, 189, 0.18)'
                  }}
                  whileHover={{ borderColor: AUREUS.green }}
                >
                  <div className="mb-6 rounded-xl overflow-hidden shadow-2xl">
                    <img
                      src={f.img}
                      alt={f.alt}
                      className="w-full h-auto rounded-xl"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{f.title}</h3>
                  <p className="opacity-80">{f.desc}</p>
                  {f.title === 'One-Tap FXRP Mint & Redeem' ? (
                    <a
                      href={FXRP_GUIDE_URL}
                      className="inline-block mt-4 font-medium"
                      style={{ color: AUREUS.green }}
                    >
                      Read the FXRP guide
                    </a>
                  ) : null}
                </motion.div>
              ) : (
                <motion.div
                  key={i}
                  className="rounded-2xl p-8 transition-all group border"
                  variants={fadeInUp}
                  style={{
                    background: 'rgba(1, 38, 61, 0.45)',
                    borderColor: 'rgba(96, 158, 189, 0.18)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.35)'
                  }}
                  whileHover={{
                    borderColor: f.color,
                    boxShadow: `0 12px 35px rgba(${parseInt(
                      f.color.slice(1, 3),
                      16
                    )},${parseInt(f.color.slice(3, 5), 16)},${parseInt(
                      f.color.slice(5, 7),
                      16
                    )},0.25)`
                  }}
                >
                  <div
                    className="inline-flex p-4 rounded-xl mb-6 transition-colors"
                    style={{
                      background: `rgba(${parseInt(
                        f.color.slice(1, 3),
                        16
                      )},${parseInt(f.color.slice(3, 5), 16)},${parseInt(
                        f.color.slice(5, 7),
                        16
                      )},0.18)`
                    }}
                  >
                    <f.Icon className="h-8 w-8" style={{ color: f.color }} />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{f.title}</h3>
                  <p className="opacity-80">{f.desc}</p>
                </motion.div>
              )
            )}
          </motion.div>
        </div>
      </section>

      <HowItWorksSection />

      <SecuritySection />

      <BuiltOnFlareSection />

      <BuiltBySection />

      {/* Final CTA */}
      <section
        className="py-32 text-center"
        style={{
          background: `linear-gradient(to bottom, ${AUREUS.navy}, rgba(70,67,211,0.08))`
        }}
      >
        <div className="mx-auto max-w-4xl px-6">
          <motion.h2
            className="text-5xl md:text-6xl font-black mb-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            Ready to <span style={{ color: AUREUS.green }}>own</span> it?
          </motion.h2>

          <motion.p
            className="text-lg opacity-80 mb-10 max-w-xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            Download Oxen Flow on the App Store — non-custodial, passkey-first,
            built for Flare.
          </motion.p>

          <motion.a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex transition-transform hover:scale-105"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            aria-label="Download Oxen Flow on the App Store"
          >
            <img
              src={appStoreBadge}
              alt="Download on the App Store"
              className="h-16 w-auto"
            />
          </motion.a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
